import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { buyCompanion, buyRelic, equipCompanion } from 'store/relicVault';
import { track } from 'firstVisit/analytics';

// The server's reason for a refused purchase, as a short code (Sol mixpanel-coverage D1 A: result_code, never text).
export const purchaseResultCode = result => {
  if (result?.success) return 'success';
  const reason = String(result?.error || '').toLowerCase();
  if (/coin|balance|afford|enough/.test(reason)) return 'not_enough_coins';
  if (/no longer|not available|unavailable|offer|sold out/.test(reason)) return 'unavailable';
  if (/own/.test(reason)) return 'already_owned';
  return 'failed';
};

// Timings from the designer's handoff (hooks/useStorePurchaseFlow.ts).
const CONFIRM_TO_SUCCESS_MS = 650; // relic icon flies into the vault
const SUCCESS_TO_CLOSE_MS = 1400;
const CHEST_PULSE_MS = 750;

/**
 * The store's confirm -> buy -> success -> auto-close purchase modal, plus the
 * vault-chest pulse after a relic lands in the vault.
 *
 * A failed purchase returns to the confirm stage and shows the reason the
 * server gave - not enough QuikCoins, or a relic that is no longer on offer -
 * rather than a generic failure.
 */
export function useStorePurchaseFlow({ token, onBalance }) {
  const dispatch = useDispatch();
  const [activeItem, setActiveItem] = useState(null); // { kind: 'relic'|'companion', id }
  const [stage, setStage] = useState(null); // 'confirm' | 'success' | null
  const [animating, setAnimating] = useState(false);
  const [vaultPulse, setVaultPulse] = useState(false);
  const [error, setError] = useState(null);

  const timers = useRef([]);
  const activeRef = useRef(null);

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
  };

  useEffect(
    () => () => {
      timers.current.forEach(t => clearTimeout(t));
    },
    [],
  );

  const open = useCallback((kind, id) => {
    const item = { kind, id };
    track('collection_item_selected', { item_id: id == null ? null : String(id), item_type: kind, currency_type: 'quikcoins' });
    activeRef.current = item;
    setError(null);
    setAnimating(false);
    setActiveItem(item);
    setStage('confirm');
  }, []);

  const openRelic = useCallback(id => open('relic', id), [open]);
  const openCompanion = useCallback(guildId => open('companion', guildId), [open]);

  const close = useCallback(() => {
    activeRef.current = null;
    setActiveItem(null);
    setStage(null);
    setAnimating(false);
    setError(null);
  }, []);

  const confirm = useCallback(() => {
    const active = activeRef.current;
    if (!active || animating) return;
    setError(null);
    setAnimating(true);

    later(async () => {
      const action =
        active.kind === 'relic'
          ? buyRelic({ token, relicId: active.id })
          : buyCompanion({ token, guildId: active.id });
      const result = await dispatch(action);
      track('collection_purchase_result', {
        item_id: active.id == null ? null : String(active.id),
        item_type: active.kind,
        currency_type: 'quikcoins',
        result_code: purchaseResultCode(result),
      });

      // The modal was closed or replaced while the purchase was in flight.
      if (activeRef.current !== active) return;

      setAnimating(false);
      if (!result?.success) {
        setError(result?.error || 'Purchase failed.');
        return;
      }
      // The server returns the balance it charged against, so nothing is guessed.
      if (typeof result.newBalance === 'number' && onBalance) onBalance(result.newBalance);
      setStage('success');

      later(() => {
        if (activeRef.current !== active) return;
        activeRef.current = null;
        setActiveItem(null);
        setStage(null);
        if (active.kind === 'relic') {
          setVaultPulse(true);
          later(() => setVaultPulse(false), CHEST_PULSE_MS);
        }
      }, SUCCESS_TO_CLOSE_MS);
    }, CONFIRM_TO_SUCCESS_MS);
  }, [animating, dispatch, onBalance, token]);

  /** Equip an owned companion from its reveal modal, then close. */
  const equip = useCallback(
    async guildId => {
      await dispatch(equipCompanion({ token, guildId }));
      close();
    },
    [close, dispatch, token],
  );

  return {
    activeItem,
    stage,
    animating,
    vaultPulse,
    error,
    openRelic,
    openCompanion,
    close,
    confirm,
    equip,
  };
}
